import * as React from 'react'
import { useState } from 'react'

import { Box, Button, TextSpinner } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import useDestroyByRange from 'modules/shift_management/components/ShiftManagement/DeleteShiftModals/useDestroyByRange'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useOnSelectEmployees } from 'modules/shift_management/hooks/useOnSelectEmployees'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import ShiftBulkDeleteForm from 'modules/shift_management/lib/forms/ShiftBulkDeleteForm'
import { getEmployeeCountCopy } from 'modules/shift_management/lib/getDescription'

import FormContent from './FormContent'

interface Props {
  onClose: () => void
}

const DeleteBulkModal = ({ onClose }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const form = useLazyRef(() => new ShiftBulkDeleteForm()).current
  const [destroyBackupShifts, setDestroyBackupShifts] = React.useState<boolean>(false)
  const [destroyAutomaticOnly, setDestroyAutomaticOnly] = React.useState<boolean>(false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = React.useState<boolean>(false)
  const [employeeIds, setSelectedEmployees] = useState<number[]>([])
  const startDate = form.get('start_at').value
  const endDate = form.get('end_at').value
  const [{ view }] = useSchedule()
  const { loading, selectableEmployeeIds } = useSelectableEmployees(view.startAt, view.endAt)

  const { onSelectEmployee } = useOnSelectEmployees({
    form,
    selectedEmployees: employeeIds,
    setSelectedEmployees,
  })

  const onCheckboxClick = () => setDestroyBackupShifts(!destroyBackupShifts)

  const employeeCount = employeeIds.length
  const warningDescription = getEmployeeCountCopy(employeeCount)

  const { onBulkDestroyShifts, submitting } = useDestroyByRange({
    startDate,
    endDate,
    employeeIds,
    destroyBackupShifts,
    destroyAutomaticOnly,
  })

  const featureFlag = hasFeatureFlag('shift_management_rest_day')

  const and = i18n.t('shift_management.add_shift.and')
  const restDaysTitle = `${i18n.t('shift_management.actions.delete_shift')} ${and} ${i18n
    .t('shift_management.sidepanel.rest_days')
    .toLowerCase()}`
  const title = featureFlag ? restDaysTitle : i18n.t('shift_management.delete_shifts')

  const description = featureFlag
    ? i18n.t('shift_management.bulk_delete.rest_days_description')
    : i18n.t('shift_management.bulk_delete.description')
  const button = featureFlag
    ? i18n.t('shift_management.actions.delete')
    : i18n.t('shift_management.delete_shifts')
  const label = featureFlag
    ? i18n.t('shift_management.bulk_delete.rest_days_label')
    : i18n.t('shift_management.bulk_delete.label')

  const disableForm =
    submitting ||
    !form.isDirty ||
    !form.get('start_at').value ||
    !form.get('end_at').value ||
    !form.get('employee_id').value

  if (isConfirmationModalOpen)
    return (
      <Modal
        illustration="delete-cross"
        title={i18n.t('shift_management.bulk_delete.confirm_deletion')}
        onSubmit={onBulkDestroyShifts}
        onClose={() => {
          setIsConfirmationModalOpen(false)
          onClose()
        }}
        footer={
          <>
            <Button
              type="secondary"
              disabled={submitting}
              onClick={() => setIsConfirmationModalOpen(false)}
            >
              {i18n.t('common.back')}
            </Button>
            <Button submit type="negative" disabled={submitting}>
              {button}
            </Button>
          </>
        }
      >
        {submitting && <TextSpinner text={i18n.t('shift_management.actions.deleting_shifts')} />}
        {!submitting && (
          <Box gap="s8">
            {i18n.tView('shift_management.bulk_delete.confirm_deletion_description', {
              start_date: <b>{startDate}</b>,
              end_date: <b>{endDate}</b>,

              count: (
                <b>
                  {employeeCount}&nbsp;{warningDescription}
                </b>
              ),
            })}
            <b>{i18n.t('shift_management.bulk_delete.confirm_deletion_warning')}</b>
          </Box>
        )}
      </Modal>
    )

  return (
    <Modal
      illustration="delete-cross"
      title={title}
      description={description}
      onSubmit={() => setIsConfirmationModalOpen(true)}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" disabled={disableForm} submit>
            {button}
          </Button>
        </>
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <FormContent
          form={form}
          label={label}
          onSelectEmployee={onSelectEmployee}
          selectedEmployeeIds={employeeIds}
          selectableEmployeeIds={selectableEmployeeIds}
          onCheckboxClick={onCheckboxClick}
          destroyBackUpShifts={destroyBackupShifts}
          setDestroyAutomaticOnly={() => setDestroyAutomaticOnly(!destroyAutomaticOnly)}
          destroyAutomaticOnly={destroyAutomaticOnly}
        />
      )}
    </Modal>
  )
}

export default observer(DeleteBulkModal)
